import { ThemeOption } from './../../../models/_core/theme-option';
import { NavigationService } from 'src/app/services/navigation/navigation.service';
import { UserState } from './../../../models/_core/user-state';
import { HelperUtilitiesService } from 'src/app/services/_core/helper-utilities/helper-utilities.service';
import { AlertController, ModalController } from '@ionic/angular';
import { environment } from '../../../../environments/environment';
import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { NotificationsService } from '../notifications/notifications.service';
import { StorageService } from '../storage/storage.service';
import moment from 'moment';
import { AuthState } from 'src/app/models/_core/auth-state';
import { SSOTokenResponse } from 'src/app/models/_core/sso-token-response';
import { UserDeviceService } from '../user-device/user-device.service';
import { Browser } from '@capacitor/browser';
import { AwsSettings } from 'src/app/models/_core/aws-settings';
import { AnalyticsService } from '../analytics/analytics.service';
import { User } from 'src/app/models/user';
import { AuthLegacyService } from '../auth-legacy/auth-legacy.service';
import { AuthSsoService } from '../auth-sso/auth-sso.service';
import {
  ClientApiTokenResponse,
  SessionTokenResponse,
  tokenFromApiAccessBody
} from 'src/app/models/_core/api-access-token';

/**
 * ID: bh-auth-service
 * Name: BH Auth Service
 * Description: Service used for managing authentication and user state
 * Version: 5
 *
 * ==============================
 * Change Log
 * ==============================
 * 2021-07-02 - MW - v1: Initial dev
 * 2021-07-13 - MW - v2: Implemented userState
 * 2021-07-27 - MW - v3: Improved open modal + alert handling; improved UX
 * 2022-05-23 - MW - v4: Updated depreciated value/error handling
 * 2022-05-27 - MW - v5: Implemented user state and theme subjects
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {

  //inject services
  private http = inject(HttpClient);
  private notifications = inject(NotificationsService);
  private storageService = inject(StorageService);
  private alertCtrl = inject(AlertController);
  private modalCtrl = inject(ModalController);
  private helpers = inject(HelperUtilitiesService);
  private navService = inject(NavigationService);
  private deviceService = inject(UserDeviceService);
  private analytics = inject(AnalyticsService);
  private authSso = inject(AuthSsoService);
  private authLegacy = inject(AuthLegacyService);


  env = environment;
  authUser: BehaviorSubject<User> = new BehaviorSubject(null);
  userState: UserState = {};
  userStateSubject: BehaviorSubject<UserState> = new BehaviorSubject({});
  themeSubject: BehaviorSubject<ThemeOption> = new BehaviorSubject('light');
  menuOpen: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private apiAccessTokenSubject = new BehaviorSubject<string | null>(null);
  private apiAccessTokenInitPromise: Promise<void> | null = null;
  private readonly sessionTokenStorageKey = 'sessionToken';
  static readonly DEV_MOCK_USER_TOKEN = 'testtoken';
  private readonly legacyGuestTokenStorageKey = 'apiAccessToken';
  apiUrl: any;
  timeoutWarningMs = 60000;
  timeoutLogoutMs = 120000;
  inactivitySubject = new BehaviorSubject<number>(0);
  inactivityTimer = null;
  targetUrl = '';
  awsCognitoClientId = this.env.awsCognitoClientId;

  constructor(
    // private http: HttpClient,
    // private notifications: NotificationsService,
    // private storageService: StorageService,
    // private alertCtrl: AlertController,
    // private modalCtrl: ModalController,
    // private helpers: HelperUtilitiesService,
    // private navService: NavigationService,
    // private deviceService: UserDeviceService,
    // private analytics: AnalyticsService,
    // private authLegacy: AuthLegacyService,
    // private authSso: AuthSsoService
  ) {
    this.getUserStateFromStorage();
  }


  mockLoginData: User[] = [{
    userId: 'en27997',
    //password: 'testpassword'
    firstName: 'James',
    lastName: 'Keeping',
    userName: 'james.keeping@baystatehealth.org',
    fullName: 'James Keeping',
    role: 'SYS_ADMIN',
    roles: ['SYS_ADMIN'],
    token: 'testtoken',
    authenticated: true,
    hide: false,
    isActive: true,
    lastLoginAt: '2026-01-12 12:00:00',
    createdAt: '2026-01-12 12:00:00',
    updatedAt: '2026-01-12 12:00:00'
  },
  {
    userId: 'en29841',
    //password: 'testpassword'
    firstName: 'John',
    lastName: 'Carey',
    userName: 'john.carey@baystatehealth.org',
    fullName: 'John Carey',
    role: 'SYS_ADMIN',
    roles: ['SYS_ADMIN'],
    token: 'testtoken',
    authenticated: true,
    hide: false,
    isActive: true,
    lastLoginAt: '2026-01-12 12:00:00',
    createdAt: '2026-01-12 12:00:00',
    updatedAt: '2026-01-12 12:00:00'
  },
  {
    userId: 'en117486',
    //password: 'testpassword'
    firstName: 'Rikin',
    lastName: 'Patel',
    userName: 'rikin.patel@baystatehealth.org',
    fullName: 'Rikin Patel',
    role: 'SYS_ADMIN',
    roles: ['SYS_ADMIN'],
    token: 'testtoken',
    authenticated: true,
    hide: false,
    isActive: true,
    lastLoginAt: '2026-01-12 12:00:00',
    createdAt: '2026-01-12 12:00:00',
    updatedAt: '2026-01-12 12:00:00'
  },
  {
    userId: 'en43211',
    //password: 'testpassword'
    firstName: 'Dave',
    lastName: 'Walto',
    userName: 'david.walto@baystatehealth.org',
    fullName: 'David Walto',
    role: 'SYS_ADMIN',
    roles: ['SYS_ADMIN'],
    token: 'testtoken',
    authenticated: true,
    hide: false,
    isActive: true,
    lastLoginAt: '2026-01-12 12:00:00',
    createdAt: '2026-01-12 12:00:00',
    updatedAt: '2026-01-12 12:00:00'
  },
];


  async loadAwsSettings(): Promise<any> {
    try {
      const settings = await firstValueFrom(this.getAwsSettings());
      console.log('loadAwsSettings', settings);
      Promise.resolve(settings);
    } catch (err) {
      console.error('loadAwsSettings: error: ', err);
      throw err;
    }
  }

  /**
   * Gets Auth User object
   * Recommend subscribing to authUser directly
   */
  getAuthUser(): User {
    const authUser = this.authUser.getValue();
    return authUser ? authUser : {};
  }

  /***
   * Updates Auth User object with provided object
   * @param authUser User object to replace existing value
   */
  setAuthUser(authUser: User) {
    this.authUser.next(authUser);
  }

  private isSamlPlaceholderUserId(userId: string | null | undefined): boolean {
    if (!userId) {
      return false;
    }
    return String(userId).toLowerCase().includes('bh-dev-saml-sso_');
  }

  /** Normalize /users/me payload into a User with userId set. */
  mergeValidatedUser(authUser: User, res: any): User | null {
    const payload = res?.user ?? res?.data ?? res?.users?.[0] ?? res;
    const userId = payload?.userId ?? payload?.id;
    if (!userId || this.isSamlPlaceholderUserId(userId)) {
      return null;
    }

    const merged: User = { ...authUser, ...(payload ?? {}) };
    merged.userId = String(userId).toLowerCase();
    merged.userName = payload.userName ?? payload.name ?? merged.userName;
    merged.fullName = payload.fullName ?? payload.name ?? merged.fullName;
    merged.firstName = this.helpers.getFirstName(merged.fullName ?? merged.userName);
    merged.limitAccess = payload.limitAccess ?? merged.limitAccess;
    merged.userAccess = payload.userAccess ?? merged.userAccess;
    merged.active = payload.active ?? merged.active;
    merged.isActive = payload.active === 1 || payload.isActive === true;
    merged.role = this.setRole(merged);
    return merged;
  }

  /**
   * Gets User State object
   * Recommend subscribing to userStateSubject directly
   */
  getUserState(): UserState {
    return this.userStateSubject.getValue();
  }

  /***
   * Updates User State subject object
   * @param userState User State to update with
   */
  setUserState(userState: UserState) {
    this.userStateSubject.next(userState);
  }

  /**
   * Toggles the menu open and closed
   */
  toggleMenu() {
    const isMenuOpen = this.menuOpen.getValue();
    this.menuOpen.next(!isMenuOpen);
  }

  /**
   * Gets active theme
   * Recommend subscribing to themeSubject directly
   */
  getTheme(): ThemeOption {
    return this.themeSubject.getValue();
  }

  /***
   * Updates theme subject object
   * @param theme ThemeOption to update with
   */
  setTheme(theme: ThemeOption) {
    this.themeSubject.next(theme);
  }


  /***
   * Gets the user's state from storage
   */
  async getUserStateFromStorage() {
    this.userState = await this.storageService.getData('userState');
    const storedUser = this.userState?.authUser;
    if (storedUser?.userId) {
      this.setAuthUser(storedUser);
      this.userStateSubject.next(this.userState);
    }
  }

  /***
   * Save the user's state to local storage
   */
  async saveUserStateToStorage() {
    if (!this.env.storeToken && this.userState.authUser && this.userState.authUser.token) {
      this.userState.authUser.token = null;
    }
    this.userStateSubject.next(this.userState);
    this.storageService.saveData('userState', this.userState);
  }

  /**
   * Starts inactivity timer.
   * Should be called after successfully logging in
   */
  public startInactivityTimer() {
    if (this.env.requireTimeout) {
      this.timeoutLogoutMs = this.env.timeoutThreshold;
      this.timeoutWarningMs = this.timeoutLogoutMs - 30000;
      this.inactivityTimer = setInterval(() => {
        let time = this.inactivitySubject.getValue();
        time += 1000;
        // console.log('Inactivity: ', time)
        this.inactivitySubject.next(time);
        this.checkForTimeout();
      }, 1000);
    }
  }

  /**
   * Check for session timeout, display appropriate alert if timing out.
   */
  public async checkForTimeout() {
    const time = this.inactivitySubject.getValue();
    if (time === this.timeoutWarningMs) {
      const alert = await this.alertCtrl.create({
        header: 'Still there?',
        message: 'You will be signed out soon due to inactivity.',
        cssClass: 'wide-alert warning',
        backdropDismiss: false,
        buttons: [
          {
            text: 'Stay signed in',
            cssClass: 'primary',
            handler: (val) => {
              this.bumpInactivityTimer();
            }
          },
          {
            text: 'Sign out',
            handler: async (val) => {
              await this.dismissAllModalsAndAlerts();
              this.logout(false, true);
            }
          }
        ]
      });
      await alert.present();
    } else if (time === this.timeoutLogoutMs) {
      await this.dismissAllModalsAndAlerts();
      this.logout(true, true);
    }
  }

  /**
   * Dismisses all open alerts and modals
   */
  async dismissAllModalsAndAlerts(): Promise<boolean> {
    // Dismiss alerts
    for (let i = 0; i < 25; i++) {
      const alert = await this.alertCtrl.getTop();
      if (alert) {
        await alert.dismiss();
      } else {
        break;
      }
    }

    // Dismiss modals
    for (let i = 0; i < 25; i++) {
      const modal = await this.modalCtrl.getTop();
      if (modal) {
        await modal.dismiss();
      } else {
        break;
      }
    }

    return Promise.resolve(true);

  }

  /**
   * Bumps activity timer, preventing auto-timeout
   */
  public bumpInactivityTimer() {
    this.inactivitySubject.next(0);
  }

  validateUser(): Observable<any> {
    if (this.env.ssoEnabled) {
      return this.authSso.validateUser();
    } else {
      return this.authLegacy.validateUser();
    }
  }

  /**
   * POST /auth/session-token — guest API access for non-SSO users (cookie-based).
   */
  createSessionToken(): Observable<SessionTokenResponse> {
    const url = `${this.env.apiUrl}/auth/session-token`;
    return this.http.post<SessionTokenResponse>(url, {}, {
      withCredentials: true,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'BH-DONT-ASSIGN-HEADERS': 'true'
      })
    });
  }

  /**
   * GET /client-token — optional access-key header when backend requires it.
   */
  getClientApiToken(): Observable<ClientApiTokenResponse> {
    const url = `${this.env.apiUrl}/auth/client-token`;
    let headers = new HttpHeaders();
    const key = this.env['clientTokenAccessKey'] as string | undefined;
    const headerName = this.env['clientTokenAccessKeyHeader'] as string | undefined;
    if (key && headerName) {
      headers = headers.set(headerName, key);
    }
    headers = headers.set('BH-DONT-ASSIGN-HEADERS', 'true');
    return this.http.get<ClientApiTokenResponse>(url, { headers, withCredentials: true });
  }

  getApiAccessToken(): string | null {
    return this.apiAccessTokenSubject.getValue();
  }

  isDevMockUserToken(token: string | null | undefined): boolean {
    return token === AuthService.DEV_MOCK_USER_TOKEN;
  }

  /** Logged-in SSO user (Cognito refresh flow applies; not guest session token). */
  isSsoAuthenticatedUser(authUser?: User): boolean {
    const user = authUser ?? this.getAuthUser();
    return !!(
      this.env.ssoEnabled &&
      user?.userId &&
      !this.isDevMockUserToken(user.token)
    );
  }

  /** Cognito id/access JWT used as API bearer for SSO users. */
  resolveSsoCognitoBearer(authUser?: User): string | null {
    const user = authUser ?? this.getAuthUser();
    const token = user?.token?.trim();
    if (!token || this.isDevMockUserToken(token)) {
      return null;
    }
    return token;
  }

  async applySsoTokenRefresh(ssoTokenRes: SSOTokenResponse): Promise<string | null> {
    const cognitoToken = ssoTokenRes.id_token ?? ssoTokenRes.access_token ?? null;
    if (!cognitoToken) {
      return null;
    }
    const authUser = this.getAuthUser();
    if (authUser?.userId) {
      const updatedUser = { ...authUser, token: cognitoToken };
      this.setAuthUser(updatedUser);
      if (this.userState?.authUser) {
        this.userState.authUser = updatedUser;
      }
      await this.saveUserStateToStorage();
    }
    if (ssoTokenRes.refresh_token?.trim()) {
      await this.storageService.saveData('refreshToken', ssoTokenRes.refresh_token.trim(), true);
    }
    return cognitoToken;
  }

  async setApiAccessToken(token: string | null): Promise<void> {
    this.apiAccessTokenSubject.next(token);
    await this.persistSessionTokenToStorage(token);
  }

  private async persistSessionTokenToStorage(token: string | null): Promise<void> {
    try {
      if (token) {
        await this.storageService.saveData(this.sessionTokenStorageKey, token, true);
        await this.storageService.removeData(this.legacyGuestTokenStorageKey);
      } else {
        await this.storageService.removeData(this.sessionTokenStorageKey);
        await this.storageService.removeData(this.legacyGuestTokenStorageKey);
      }
    } catch (err) {
      console.error('AuthService: persistSessionTokenToStorage', err);
    }
  }

  private async loadSessionTokenFromStorage(): Promise<void> {
    try {
      let raw = await this.storageService.getData(this.sessionTokenStorageKey, true);
      let token = typeof raw === 'string' && raw.length > 0 ? raw : null;
      if (!token) {
        raw = await this.storageService.getData(this.legacyGuestTokenStorageKey, true);
        token = typeof raw === 'string' && raw.length > 0 ? raw : null;
        if (token) {
          await this.storageService.saveData(this.sessionTokenStorageKey, token, true);
          await this.storageService.removeData(this.legacyGuestTokenStorageKey);
        }
      }
      if (token) {
        this.apiAccessTokenSubject.next(token);
      }
    } catch {
      /* noop */
    }
  }

  /**
   * Obtain a guest/API access token on startup: session-token first, then client-token if configured.
   */
  async initializeApiAccessToken(): Promise<void> {
    if (this.apiAccessTokenInitPromise) {
      return this.apiAccessTokenInitPromise;
    }
    this.apiAccessTokenInitPromise = this.doInitializeApiAccessToken();
    try {
      await this.apiAccessTokenInitPromise;
    } finally {
      this.apiAccessTokenInitPromise = null;
    }
  }

  private async doInitializeApiAccessToken(): Promise<void> {
    await this.loadSessionTokenFromStorage();

    const applyFromSession = async (): Promise<boolean> => {
      try {
        const res = await firstValueFrom(
          this.createSessionToken().pipe(catchError(() => of(null)))
        );
        const tok = tokenFromApiAccessBody(res);
        if (tok) {
          await this.setApiAccessToken(tok);
          return true;
        }
      } catch {
        /* noop */
      }
      return false;
    };

    const applyFromClient = async (): Promise<boolean> => {
      const key = this.env['clientTokenAccessKey'] as string | undefined;
      if (!key) {
        return false;
      }
      try {
        const res = await firstValueFrom(
          this.getClientApiToken().pipe(catchError(() => of(null)))
        );
        const tok = tokenFromApiAccessBody(res);
        if (tok) {
          await this.setApiAccessToken(tok);
          return true;
        }
      } catch {
        /* noop */
      }
      return false;
    };

    if (await applyFromSession()) {
      return;
    }
    if (await applyFromClient()) {
      return;
    }
    console.warn(
      'AuthService: no API access token from session-token or client-token (endpoint disabled or not configured)'
    );
  }

  getAwsSettings(): Observable<AwsSettings> {
    const url = `${this.env.apiUrl}/settings`;
    return this.http.get(url).pipe(
      map((data: any) => {
        console.log('getAwsSettings', data);
        return data;
      }),
      // catchError(error => of(error))
    );
  }

  async authorizeRedirect() {
    // const settings = await firstValueFrom(this.getAwsSettings());
    // console.log('*** authorizeRedirect: settings: ', settings);
    const redirectRoot = this.getUriFromLocation();
    const redirectUri = redirectRoot + '/redirect';
    const url = `${this.env.awsCognitoUrl}/oauth2/authorize?` +
      `response_type=code&client_id=${this.awsCognitoClientId}&scope=openid%20profile&` +
      `redirect_uri=${redirectUri}`;

    // this.notifications.showAlert('URL', redirectRoot);
    console.log('*** authorizeRedirect: ', url);
    console.log('authorizeRedirect', url);
    location.href = url;
  }

  async openAuthBrowser(url): Promise<void> {
    await Browser.addListener('browserPageLoaded', () => {
      // Do something when browser page is loaded
      console.log('browser page finished loading', Browser);
    });
    // await Browser.addListener('urlChangeEvent', () => {

    // });
    await Browser.open({
      windowName: 'auth',
      url,
      presentationStyle: 'popover'
    });
    return Promise.resolve();
  }

  getSSOToken(authCode: string): Observable<SSOTokenResponse> {
    const redirectRoot = this.getUriFromLocation();
    const redirectUri = redirectRoot + '/redirect';
    const url = `${this.env.awsCognitoUrl}/oauth2/token?client_id=${this.awsCognitoClientId}`;
    const body = {
      grant_type: 'authorization_code',
      code: authCode,
      redirect_uri: redirectUri
    };
    const encodedBody = this.convertToFormData(body);
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      'BH-DONT-ASSIGN-HEADERS': 'true'
    });
    return this.http.post(url, encodedBody, { headers }).pipe(
      map((data: any) => {
        return data;
      }),
      // catchError(error => of(error))
    );
  }

  refreshSSOToken(refreshToken: string): Observable<SSOTokenResponse> {
    const url = `${this.env.awsCognitoUrl}/oauth2/token?client_id=${this.awsCognitoClientId}`;
    const body = {
      refresh_token: refreshToken,
      grant_type: 'refresh_token'
    };
    const encodedBody = this.convertToFormData(body);
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      'BH-DONT-ASSIGN-HEADERS': 'true',
      'BH-TOKEN-REFRESH': 'true'
    });
    return this.http.post<SSOTokenResponse>(url, encodedBody, { headers });
  }

  private convertToFormData(data: any): string {
    const formData = new URLSearchParams();

    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        formData.set(key, data[key]);
      }
    }

    return formData.toString();
  }

  /***
   * Logs user into application
   * @param userName User Name
   * @param password  Password
   * @returns User Login Payload
   */
  login(userId, password): Observable<any> {
    // const url = `${this.env.apiUrl}/login`;
    // const body = {
    //   userId,
    //   password,
    // };
    // return this.http.post(url, body, { observe: 'response'}).pipe(
    //   map((data: any) => {
    //     this.handleLoginResponse(data.body);
    //     return data.body;
    //   }),
    // );
    const mockUser = this.mockLoginData.find(user => user.userId.toLowerCase() === userId.toLowerCase());
    if (mockUser) {
      // Create a response-like object with x_status for compatibility
      const mockResponse = {
        ...mockUser,
        x_status: 'S' // Success status
      };

      return of(mockResponse).pipe(
        map((data: any) => {
          this.handleLoginResponse(data);
          return data;
        }),
      );
    } else {
      // User not found in mock data
      return of({
        x_status: 'E',
        error: {
          message: 'Invalid user ID or password'
        }
      });
    }
  }

  /***
   * Process user response data, determining login status
   * @param data Login Response Data
   */
  async handleLoginResponse(data: any) {
    if (data) {
      this.startInactivityTimer();
      const authUser: User = data;
      const userId = (authUser.id || authUser.userId);
      authUser.userId = userId.toLowerCase();
      authUser.firstName = this.helpers.getFirstName(data.fullName);
      authUser.role = this.setRole(authUser);

      this.setAuthUser(authUser);
      this.userState.sessionAppVersion = this.env.appVersion + '-' + this.env.env;
      this.userState.userId = authUser.userId;
      this.userState.environment = this.env;
      this.userState.lastLoggedIn = moment().format('M/D/YYYY HH:mm');
      this.userState.authState = AuthState.LOGGED_IN;
      if (this.env.storeToken) {
        this.userState.authUser = authUser;
      }
      this.saveUserStateToStorage();
      void this.initializeApiAccessToken();
    }
    return;
  }

  async initSession(ssoTokenRes: SSOTokenResponse): Promise<void> {
    try {
      const cognitoToken = ssoTokenRes.id_token ?? ssoTokenRes.access_token;
      if (!cognitoToken) {
        throw new Error('SSO response did not include an id token');
      }

      let authUser = this.getAuthUser();
      authUser.token = cognitoToken;
      this.setAuthUser(authUser);

      const res = await firstValueFrom(this.validateUser());
      const validatedUser = this.mergeValidatedUser(authUser, res);
      if (!validatedUser) {
        throw new Error(
          `SSO session could not be validated${res ? '' : ' (no profile returned from /users/me)'}`
        );
      }

      authUser = validatedUser;
      authUser.token = cognitoToken;
      this.setAuthUser(authUser);
      this.analytics.analyticsData.userid = (authUser.userName ?? '').toLowerCase();
      this.userState.sessionAppVersion = this.env.appVersion + '-' + this.env.env;
      this.userState.userId = authUser.userId;
      this.userState.userName = authUser.userName;
      this.userState.environment = this.env;
      this.userState.lastLoggedIn = moment().format('M/D/YYYY HH:mm');
      this.userState.authState = AuthState.LOGGED_IN;
      this.userState.authUser = authUser;
      await this.saveUserStateToStorage();
      await this.storageService.saveData('refreshToken', ssoTokenRes.refresh_token, true);
      return Promise.resolve();
    } catch (err) {
      throw err;
    }
  }

  setRole(authUser: User): 'USER' | 'ADMIN' | 'SYS_ADMIN' | 'EDITOR' {
    if (authUser.roles) {
      if (authUser.roles.includes('SYS_ADMIN') || authUser.roles.includes('SYSADMIN')) {
        return 'SYS_ADMIN';
      } else if (authUser.roles.includes('ADMIN')) {
        return 'ADMIN';
      } else if (authUser.roles.includes('EDITOR')) {
        return 'EDITOR';
      } else {
        return 'USER';
      }
    } else if (authUser.role) {
      const role = String(authUser.role).toUpperCase();
      if (role === 'SYSADMIN' || role === 'SYS_ADMIN') {
        return 'SYS_ADMIN';
      }
      if (role === 'ADMIN') {
        return 'ADMIN';
      }
      if (role === 'EDITOR') {
        return 'EDITOR';
      }
      return 'USER';
    } else {
      return 'USER';
    }
  }

  getUserRoles(authUser: User): string[] {
    if (!authUser) {
      return [];
    }
    if (authUser.roles?.length) {
      return authUser.roles;
    }
    if (authUser.role) {
      return [authUser.role];
    }
    return [];
  }

  isAdminOrSysAdmin(authUser?: User): boolean {
    const userRoles = this.getUserRoles(authUser ?? this.getAuthUser());
    return userRoles.some(role =>
      role === 'ADMIN' || role === 'SYS_ADMIN' || role === 'SYSADMIN'
    );
  }

  resolvePostLoginPath(): string {
    if (this.isAdminOrSysAdmin()) {
      return '/tabs/admin-landing';
    }
    return this.navService.homeNavPage?.navPath || '/tabs/patient-landing/BMCLD';
  }

  /**
   * Best-effort JWS payload parse. Returns null if the string is not a decodable JWT
   * (opaque API tokens, malformed payloads, or non-UTF8 claims must not throw).
   */
  decodeJwt(token: string): any {
    if (!token) {
      return null;
    }
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    const base64Url = parts[1];
    if (!base64Url) {
      return null;
    }
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) {
      base64 += '='.repeat(4 - pad);
    }
    try {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const jsonPayload = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }

  isTokenExpired(token: string): boolean {
    const decodedToken = this.decodeJwt(token);
    // console.log('authService: isTokenExpired: ', decodedToken);

    if (decodedToken && decodedToken.exp) {
      const expirationTime = decodedToken.exp * 1000; // Convert to milliseconds
      const currentTime = new Date().getTime();
      // const expireMoment = moment(expirationTime);
      // console.log('authService: expireMoment: ', expireMoment.format('M/D/YYYY h:mm a'));

      return expirationTime <= currentTime;
    }

    // If the 'exp' claim is not present, consider the token as not expired
    return false;
  }

  getUriFromLocation(): string {
    // Get the current location
    const currentLocation = window.location;

    // Extract protocol, domain, and port
    const protocol = currentLocation.protocol;
    const domain = currentLocation.hostname;
    const port = currentLocation.port;
    const portSuffix = (port && port !== '80') ? ':' + port : '';

    console.log("Protocol:", protocol);
    console.log("Domain:", domain);
    console.log("Port:", port);

    if (protocol === 'capacitor:' || this.deviceService.isNotBrowser()) {
      // Return web url for universal links
      return this.env.webUrl;
    } else {
      // Return localhost
      return protocol + '//' + domain + portSuffix;
    }

    // return protocol + '//' + domain + portSuffix;

  }

  /***
   * Logs user out
   * @param isExpired Determines if session expired
   * @param redirectToLogin Designates redirection to login page
   */
  async logout(isExpired = false, redirectToLogin = true) {
    this.authUser.next(null);
    this.inactivitySubject.next(0);
    clearInterval(this.inactivityTimer);
    this.inactivityTimer = null;
    this.navService.navPages = [];
    this.alertCtrl.getTop().then(alert => {
      if (alert) {
        alert.dismiss();
      }
    });
    if (isExpired) {
      this.userState.authState = AuthState.EXPIRED;
      this.notifications.showAlert('Session expired', 'You were signed out due to inactivity.', 'danger');
    } else {
      this.userState.authState = AuthState.LOGGED_OUT;
    }

    this.apiAccessTokenSubject.next(null);
    await this.storageService.removeData(this.sessionTokenStorageKey);
    await this.storageService.removeData(this.legacyGuestTokenStorageKey);
    this.storageService.removeData('userState');

    void this.initializeApiAccessToken();

    if (redirectToLogin) {
      this.navService.navigateBack('login');
    }
  }
}
